import React, { useEffect } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { MarketingPrompt } from "../components/MarketingConsent";
import { ArrowUpRight } from "lucide-react";
import { api, Collection, Order, Wallet } from "../lib/api";
import { RecurringPanel } from "../components/RecurringPanel";
import { OptionExplainer } from "../components/OptionExplainer";
import { ProfileCard } from "../components/ProfileCard";
import { FestiveBanner } from "../components/FestiveBanner";
import { CreditSection } from "../components/cellar/CreditSection";
import { money } from "../lib/format";
import { useAuth } from "../context/AuthContext";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { SuggestedCases } from "../components/AdviserExtras";
import { FaqWidget } from "../components/FaqWidget";
import { MyPrivateEvents } from "../components/MyPrivateEvents";
import { useMyAdviser } from "../components/AdviserCard";

const scrollTo = (id: string) => setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" }), 150);

export default function DashboardPage() {
  const { user } = useAuth();
  const { hash } = useLocation();
  const [params, setParams] = useSearchParams();
  const { data: wallet } = useQuery<Wallet>({ queryKey: ["wallet"], queryFn: () => api.get("/wallet").then((r) => r.data) });
  const { data: adviser } = useMyAdviser();
  const { data: orders } = useQuery<Order[]>({ queryKey: ["orders"], queryFn: () => api.get("/orders").then((r) => r.data) });
  const { data: collections } = useQuery<Collection[]>({ queryKey: ["collections"], queryFn: () => api.get("/catalog/collections").then((r) => r.data) });

  useEffect(() => { if (hash === "#profile") scrollTo("profile"); }, [hash]);
  useEffect(() => { if (params.get("tab") || params.get("payment")) scrollTo("credit"); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const openTab = (tab: string) => { setParams((p) => { p.set("tab", tab); return p; }); scrollTo("credit"); };

  const featured = (collections || []).filter((c) => c.featured).slice(0, 9);
  const firstName = user && typeof user === "object" ? user.name.split(" ")[0] : "";

  return (
    <div className="space-y-16" data-testid="dashboard-page">
      <MarketingPrompt />
      <SuggestedCases />
      <ProfileCard />
      <FestiveBanner />
      <MyPrivateEvents />

      <section className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-7 rise">
          <div className="eyebrow">Good to see you, {firstName}</div>
          <h1 className="mt-4 font-serif text-4xl font-medium tracking-tight sm:text-5xl lg:text-6xl">Your account balance is</h1>
          <div className="mt-6 font-serif text-6xl font-medium tabular-nums tracking-tight sm:text-7xl lg:text-8xl" data-testid="dashboard-balance">
            {wallet ? money(wallet.total_pence) : "—"}
          </div>
          {wallet && wallet.club_balance_pence > 0 && <div className="mt-3 text-sm text-neutral-600" data-testid="dashboard-split">{money(wallet.balance_pence)} cellar credit · {money(wallet.club_balance_pence)} Wine Club credit</div>}
          <div className="mt-6 flex flex-wrap items-center gap-6 text-sm text-neutral-600">
            <span data-testid="dashboard-pending">{wallet && wallet.pending_pence > 0 ? `${money(wallet.pending_pence)} pending with GoCardless` : "No payments pending"}</span>
            <span className="h-4 w-px bg-border" />
            <span className="flex items-center gap-2">Direct Debit <StatusBadge status={wallet?.mandate_status || "none"} testId="dashboard-mandate-status" /></span>
          </div>
          {wallet?.flagged && <p className="mt-4 border border-[var(--wine)] p-3 text-sm text-[var(--wine)]" data-testid="dashboard-flagged">A payment on your account was reversed. Ordering is paused while we review it.</p>}
          <div className="mt-10 flex flex-wrap gap-3">
            <Button className="rounded-none px-8 py-6 hover-lift" onClick={() => openTab("topup")} data-testid="dashboard-topup-button">Add credit</Button>
            <Button asChild variant="outline" className="rounded-none px-8 py-6 hover-lift"><Link to="/shop" data-testid="dashboard-shop-button">Browse wines</Link></Button>
            <button onClick={() => openTab("ledger")} className="flex items-center gap-1 self-center text-sm underline underline-offset-4" data-testid="dashboard-ledger-link">Full ledger <ArrowUpRight className="h-4 w-4" /></button>
          </div>
        </div>
        <div className="space-y-px lg:col-span-5 lg:mt-24">
          <RecurringPanel />
          <div className="border border-t-0 border-border p-6 rise" style={{ animationDelay: "140ms" }} data-testid="dashboard-orders-card">
            <div className="eyebrow">Recent orders</div>
            {orders && orders.length > 0 ? (
              <ul className="mt-3 space-y-2">
                {orders.slice(0, 3).map((o) => (
                  <li key={o.id} className="flex items-center justify-between text-sm">
                    <span>{o.shopify_order_name || o.id.slice(-6).toUpperCase()} · {o.items.length} line{o.items.length > 1 ? "s" : ""}</span>
                    <span className="flex items-center gap-3"><StatusBadge status={o.status} /><span className="tabular-nums">{money(o.total_pence)}</span></span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="mt-2 text-sm text-neutral-600">Nothing ordered yet.</div>
            )}
          </div>
          <Link to="/contact" className="group flex items-center justify-between border border-t-0 border-border bg-[var(--ink)] p-6 text-[var(--paper)] transition-colors duration-200 hover:bg-[var(--wine)] rise" style={{ animationDelay: "200ms" }} data-testid="dashboard-adviser-card">
            <div>
              <div className="eyebrow !text-[var(--paper)]/60">Personal service</div>
              <div className="mt-1 font-serif text-xl" data-testid="dashboard-adviser-name">{adviser ? `Speak to ${adviser.first_name}` : "Speak to the Hourlier family"}</div>
              <div className="mt-1 text-sm text-[var(--paper)]/70">Request a call back or send a note — we'll build the case for you.</div>
            </div>
            <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:-translate-y-1 group-hover:translate-x-1" />
          </Link>
        </div>
      </section>

      <CreditSection />

      <OptionExplainer />

      <Link to="/make-me-a-case" className="group mt-10 flex flex-wrap items-center justify-between gap-4 border border-border bg-[var(--ink)] p-6 text-[var(--paper)] transition-colors duration-200 hover:bg-[var(--wine)]" data-testid="dashboard-make-case-card">
        <div><div className="eyebrow text-[var(--paper)]/70">Clearance cellar</div><div className="mt-1 font-serif text-2xl">Make me a case</div><p className="mt-1 max-w-xl text-sm text-[var(--paper)]/80">Tell us what you like and we'll build twelve bottles from wines we need to move on — at a clearance price, paid from your cellar.</p></div>
        <span className="font-brand text-[12px] font-bold uppercase tracking-[0.14em]">Build mine →</span>
      </Link>

      <FaqWidget audience="cellar" eyebrow="Cellar Plan · good to know" title="How your cellar credit works" tone="cellar" />

      <section>
        <div className="eyebrow">Hidden France</div>
        <h2 className="mt-2 font-serif text-2xl sm:text-3xl">Browse by region</h2>
        <div className="mt-8 grid grid-cols-2 gap-px md:grid-cols-3 lg:grid-cols-6">
          {featured.map((c, i) => (
            <Link key={c.handle} to={`/shop?collection=${c.handle}`} className={`group relative flex overflow-hidden border border-border bg-[var(--parchment)] p-6 hover-lift rise ${i === 0 ? "col-span-2 row-span-2 items-end" : "items-end"}`} style={{ animationDelay: `${i * 60}ms`, minHeight: i === 0 ? 300 : 150 }} data-testid={`region-tile-${c.handle}`}>
              {c.image && <img src={c.image} alt="" className="absolute inset-0 h-full w-full object-cover opacity-[0.18] transition-transform duration-700 group-hover:scale-105" />}
              <div className="relative">
                <div className="eyebrow">{c.products_count ? `${c.products_count} wines` : "Collection"}</div>
                <div className={`mt-1 font-serif leading-tight ${i === 0 ? "text-3xl" : "text-lg"}`}>{c.title}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
